import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import {
  AccountFeature,
  AccountFeatureState,
  useAccountFeatures,
} from '@dbt-labs/app-nav';

import { Account, useAccount } from '@/hooks/admin/account';
import { UserResponse, useUser } from '@/hooks/admin/user';
import { useRawParams } from '@/hooks/params';
import { mockFeatures } from '../../accountfeatures';
import { useDevelopmentContext } from './DevelopmentContextProvider';

export type DbtCloudProject = {
  name: string;
  id: number;
  defaultEnvironmentId?: number | undefined;
};

export type DbtCloudAccountData = {
  accountName: string;
  // TODO: projectName should be undefine-able in dbtCloud, but not dbtCloudProject
  /** @deprecated use useDbtCloudProject for project information*/
  projectName: string;
  hasAccountFeature: (feature: AccountFeature) => boolean;
  defaultEnvironmentId: number | 'NO_DEFAULT_ENVIRONMENT' | undefined;
  getProjectName: (projectId: number) => string | undefined;
  dbtCloudProjects: DbtCloudProject[] | undefined;
  hasProjectAccess: (projectId: number) => boolean;
  accountId: number | undefined;
  account: Account | undefined;
  hasDeveloperLicense: boolean;
  isAdmin: boolean;
  appContext: AppContext;
  urlPrefix?: string;
  userHasPermissions: (permissionName: string) => boolean;
  user: UserResponse['user'] | undefined;
  /** Do not pass this in directly to a Discovery API query, rely on userId from useEnvironmentScope instead */
  userId: number | 'loading';
};

const DbtCloudContextAccount = createContext<DbtCloudAccountData>({
  accountName: 'My Account',
  hasAccountFeature: (_feature: AccountFeature) => false,
  projectName: 'My Project',
  defaultEnvironmentId: undefined,
  getProjectName: () => undefined,
  dbtCloudProjects: undefined,
  hasProjectAccess: () => true,
  accountId: undefined,
  account: undefined,
  hasDeveloperLicense: false,
  isAdmin: false,
  appContext: 'dbt-cloud',
  userHasPermissions: () => false,
  user: undefined,
  userId: 0, // What is a reasonable default?
});

/**
 * Gets all of the dbt Cloud state which we can get access to.
 * For instance, accountId should be present, but projectId may
 * not be present if you're not in a project-scoped URL.
 */
export const useDbtCloudAccount = () => {
  return useContext(DbtCloudContextAccount);
};

/**
 * These are the app contexts which are missing the basename
 * on the `<BrowserRouter>`. These are the apps which
 * must explicitly include `/explore` in their URLs.
 */
const MISSING_BASENAME: AppContext[] = ['cloud-ui', 'snowflake-app'];

export const DEFAULT_URL_PREFIX = '/explore';

export const useExplorePathPrefix = (): string => {
  const { appContext, urlPrefix } = useDbtCloudAccount();
  const shouldPrefix = MISSING_BASENAME.includes(appContext);
  return shouldPrefix ? (urlPrefix ?? DEFAULT_URL_PREFIX) : '';
};

/**
 * This function determines if a user has a specific permission for a given account.
 * TODO: Implement a function that can determine multiple permissions in one call.
 */
const userHasPermissions = ({
  accountId,
  user,
  permissionName,
}: {
  accountId: number;
  user: UserResponse['user'] | undefined;
  permissionName: string;
}) => {
  if (!user || !user.permissions) {
    return false;
  }
  const accountPermissions = user.permissions.find((p) => p.account_id === accountId);
  if (!accountPermissions || !accountPermissions.permission_statements) {
    return false;
  }
  const permission = accountPermissions.permission_statements.find(
    (p) => p.permission === permissionName,
  );
  return permission?.all_resources || permission?.target_resource === accountId;
};

type AppContext =
  /** Vertical Slice */
  | 'dbt-cloud'
  /** Embedded snowflake app */
  | 'snowflake-app'
  /** Embedded in cloud-ui */
  | 'cloud-ui';

export type DbtCloudAccountParams = {
  accountName: string | undefined;
  projectName: string | undefined;
  dbtCloudProjects: DbtCloudProject[] | undefined;
  defaultEnvironmentId: number | 'NO_DEFAULT_ENVIRONMENT' | undefined;
  hasDeveloperLicense: boolean;
  isAdmin: boolean;
  appContext: AppContext;
  urlPrefix: string | undefined;
};

export const DbtCloudAccountProvider: FC<PropsWithChildren<DbtCloudAccountParams>> = ({
  accountName,
  appContext,
  projectName,
  dbtCloudProjects,
  defaultEnvironmentId,
  hasDeveloperLicense,
  isAdmin,
  children,
  urlPrefix,
}) => {
  const projectNameMap = useMemo(() => {
    return (
      dbtCloudProjects?.reduce(
        (acc: Record<number, string>, project: DbtCloudProject) => {
          acc[project.id] = project.name;
          return acc;
        },
        {},
      ) ?? {}
    );
  }, [dbtCloudProjects]);

  const getProjectName = useCallback(
    (id: number) => {
      return projectNameMap?.[id];
    },
    [projectNameMap],
  );

  const hasProjectAccessMap = useMemo(() => {
    return (
      dbtCloudProjects?.reduce(
        (acc: Record<number, boolean>, project: DbtCloudProject) => {
          acc[project.id] = true;
          return acc;
        },
        {},
      ) ?? {}
    );
  }, [dbtCloudProjects]);

  const hasProjectAccess = useCallback(
    (id: number) => {
      return hasProjectAccessMap?.[id];
    },
    [hasProjectAccessMap],
  );

  const { accountId } = useRawParams();

  const devData = useDevelopmentContext();
  let account = useAccount(accountId || 0).data;
  // at time of writing, `account` needs to be defined for MultiProjectNavel to render.
  if (!account && devData.isDev) {
    account = {
      id: accountId || 0,
      name: accountName || 'My Account',
      plan: 'enterprise', // mocking an enterprise account, change as needed.
      discovery_api_url: '',
    };
  }

  const currentUserQuery = useUser(true);

  const results = useAccountFeatures(accountId);
  const enabledFeatures =
    import.meta.env.MODE === 'development' &&
    import.meta.env.VITE_MOCK_ACCOUNT_FEATURES === 'true'
      ? { ...results.data, ...mockFeatures }
      : results.data;

  const hasAccountFeature = (feature: AccountFeature) => {
    return enabledFeatures ? (enabledFeatures as AccountFeatureState)[feature] : false;
  };

  // TODO: projectName should be undefine-able in dbtCloud, but not dbtCloudProject
  if (accountName == null || projectName == null || enabledFeatures == null)
    return null;
  return (
    <DbtCloudContextAccount.Provider
      value={{
        appContext,
        accountId,
        account,
        hasAccountFeature,
        accountName,
        projectName,
        defaultEnvironmentId,
        getProjectName,
        dbtCloudProjects,
        hasProjectAccess,
        hasDeveloperLicense,
        isAdmin,
        urlPrefix,
        user: currentUserQuery.data?.user,
        userHasPermissions: (permissionName: string) =>
          userHasPermissions({
            accountId: accountId || 0,
            user: currentUserQuery.data?.user,
            permissionName,
          }),
        userId: currentUserQuery.data ? currentUserQuery.data.user.id : 'loading',
      }}
    >
      {children}
    </DbtCloudContextAccount.Provider>
  );
};

export const useAllowDbtCloudLinks = () => {
  const { appContext } = useDbtCloudAccount();
  return appContext !== 'snowflake-app';
};
